if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.loadSequence = 0;
    }

    hide(preventFocus = false) {
      this.cancelPendingLoad();
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    disconnectedCallback() {
      this.cancelPendingLoad();
    }

    setOpenerLoading(opener, loading) {
      if (!opener) return;
      if (loading) opener.setAttribute('aria-disabled', 'true');
      else opener.removeAttribute('aria-disabled');
      opener.classList.toggle('loading', loading);
      opener.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !loading);
    }

    cancelPendingLoad() {
      this.loadSequence += 1;
      this.loadController?.abort();
      this.loadController = null;
      this.setOpenerLoading(this.loadingOpener, false);
      this.loadingOpener = null;
      this.modalContent?.removeAttribute('aria-busy');
    }

    async show(opener) {
      // A closed, replaced or detached preview must not reopen after a late response.
      this.cancelPendingLoad();
      const sequence = this.loadSequence;
      const controller = new AbortController();
      this.loadController = controller;
      this.loadingOpener = opener;
      this.setOpenerLoading(opener, true);
      this.modalContent.setAttribute('aria-busy', 'true');
      const isCurrent = () => sequence === this.loadSequence && !controller.signal.aborted
        && this.isConnected && opener.isConnected;

      try {
        const response = await fetch(opener.getAttribute('data-product-url'), { signal: controller.signal });
        if (!response.ok) throw new Error('Product preview request failed');
        const responseText = await response.text();
        if (!isCurrent()) return;
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
        if (!this.productElement?.dataset.section) throw new Error('Product preview is missing');
        this.preventDuplicatedIDs();
        this.removeDOMElements();
        if (this.productElement.querySelector('media-gallery')) this.productElement.querySelector('media-gallery').dataset.disablePrepend = 'false';
        this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        this.removeGalleryListSemantic();
        this.updateImageSizes();
        this.setupGalleryThumbnails();
        this.preventVariantURLSwitching();
        super.show(opener);
      } catch (error) {
        if (!isCurrent() || error?.name === 'AbortError') return;
        this.showLoadError(opener);
        super.show(opener);
      } finally {
        if (sequence === this.loadSequence) {
          this.setOpenerLoading(opener, false);
          this.modalContent.removeAttribute('aria-busy');
          this.loadingOpener = null;
          this.loadController = null;
        }
      }
    }

    showLoadError(opener) {
      // A failed preview is not proof of stock, publication or market status.
      const panel = document.createElement('div');
      panel.className = 'quick-add__error';
      const heading = document.createElement('h2');
      heading.className = 'h3';
      heading.textContent = 'This product preview could not load.';
      const message = document.createElement('p');
      message.setAttribute('role', 'status');
      message.textContent = 'Try again, or open the product page.';
      const actions = document.createElement('div');
      actions.className = 'quick-add__error-actions';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button';
      retry.textContent = 'Try again';
      retry.addEventListener('click', () => {
        retry.disabled = true;
        this.show(opener);
      });
      const link = document.createElement('a');
      link.className = 'button button--secondary';
      link.href = opener.getAttribute('data-product-url');
      link.textContent = 'Open product page';
      actions.append(retry, link);
      panel.append(heading, message, actions);
      this.modalContent.replaceChildren(panel);
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const stickyAtc = this.productElement.querySelector('sticky-atc');
      if (stickyAtc) stickyAtc.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      if (!product) return;
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }

    setupGalleryThumbnails() {
      const gallery = this.modalContent.querySelector('media-gallery');
      if (!gallery || gallery.querySelector('.quick-add-gallery-thumbnails')) return;

      const mediaImages = Array.from(gallery.querySelectorAll('.product__media-item:not(.hidden) .product__media img')).slice(0, 3);
      if (mediaImages.length < 2) return;

      // Snapshot the original attributes before the display image is changed.
      // mediaImages[0] is also the destination: reading it later loses the first image.
      const mediaSources = mediaImages.map((image) => Object.fromEntries(
        ['src', 'srcset', 'sizes', 'alt'].map((attribute) => [attribute, image.getAttribute(attribute)])
      ));
      const primaryImage = mediaImages[0];
      const mediaContainer = primaryImage.closest('.product__media') || primaryImage.parentElement;
      const thumbnails = document.createElement('div');
      const controls = document.createElement('div');
      const previousButton = document.createElement('button');
      const nextButton = document.createElement('button');
      let activeIndex = 0;

      thumbnails.className = 'quick-add-gallery-thumbnails';
      thumbnails.setAttribute('role', 'group');
      thumbnails.setAttribute('aria-label', 'Product images');
      controls.className = 'quick-add-gallery-controls';

      previousButton.type = 'button';
      previousButton.className = 'quick-add-gallery-nav quick-add-gallery-nav--previous';
      previousButton.setAttribute('aria-label', 'Show previous product image');
      previousButton.innerHTML = '<span aria-hidden="true">&lsaquo;</span>';

      nextButton.type = 'button';
      nextButton.className = 'quick-add-gallery-nav quick-add-gallery-nav--next';
      nextButton.setAttribute('aria-label', 'Show next product image');
      nextButton.innerHTML = '<span aria-hidden="true">&rsaquo;</span>';

      const setActiveMedia = (index) => {
        activeIndex = (index + mediaImages.length) % mediaImages.length;
        Object.entries(mediaSources[activeIndex]).forEach(([attribute, value]) => {
          if (value === null) primaryImage.removeAttribute(attribute);
          else primaryImage.setAttribute(attribute, value);
        });

        thumbnails.querySelectorAll('.quick-add-gallery-thumbnail').forEach((item, itemIndex) => {
          const isActive = itemIndex === activeIndex;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      };

      previousButton.addEventListener('click', () => setActiveMedia(activeIndex - 1));
      nextButton.addEventListener('click', () => setActiveMedia(activeIndex + 1));
      controls.append(previousButton, nextButton);

      if (mediaContainer) {
        mediaContainer.classList.add('quick-add-gallery-media');
        mediaContainer.appendChild(controls);
      }

      mediaImages.forEach((image, index) => {
        const button = document.createElement('button');
        const thumbnail = image.cloneNode(false);

        button.type = 'button';
        button.className = `quick-add-gallery-thumbnail${index === 0 ? ' is-active' : ''}`;
        button.setAttribute('aria-label', image.alt ? `Show ${image.alt}` : `Show product image ${index + 1}`);
        button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');

        thumbnail.removeAttribute('id');
        thumbnail.removeAttribute('srcset');
        thumbnail.setAttribute('loading', 'lazy');
        thumbnail.setAttribute('sizes', '72px');

        button.appendChild(thumbnail);
        button.addEventListener('click', () => setActiveMedia(index));

        thumbnails.appendChild(button);
      });

      const viewer = gallery.querySelector('[id^="GalleryViewer"]') || gallery;
      viewer.insertAdjacentElement('afterend', thumbnails);
    }
  });
}
